const BR_OUR_STORY_SELECTOR = '[data-br-our-story-timeline]';

function initBrOurStoryMotion(scope = document) {
  const timelines = scope.querySelectorAll(`${BR_OUR_STORY_SELECTOR}:not([data-br-our-story-motion-ready])`);

  timelines.forEach((timeline) => {
    timeline.setAttribute('data-br-our-story-motion-ready', 'true');

    const pathItems = [1, 2, 3, 4, 5]
      .map((index) => ({
        path: timeline.querySelector(`[data-br-our-story-path="${index}"]`),
        firefly: timeline.querySelector(`[data-br-our-story-firefly="${index}"]`),
      }))
      .filter((item) => item.path && item.firefly);
    const journey = timeline.querySelector('[data-br-our-story-horizontal-journey]');
    const track = timeline.querySelector('[data-br-our-story-horizontal-track]');
    const horizontalFirefly = timeline.querySelector('[data-br-our-story-horizontal-firefly]');
    const horizontalTimeline = timeline.querySelector('.br-our-story__horizontal-timeline');
    const horizontalViewport = timeline.querySelector('.br-our-story__horizontal-viewport');
    const stages = track ? Array.from(track.querySelectorAll('.br-our-story__stage')) : [];
    const dots = Array.from(timeline.querySelectorAll('[data-br-our-story-dot]'));
    const nextButton = timeline.querySelector('[data-br-our-story-next]');

    if (!pathItems.length && (!journey || !track || !horizontalFirefly || !stages.length)) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const pageWrapper = timeline.closest('.page-wrapper');
    const pageWrapperStyle = pageWrapper ? window.getComputedStyle(pageWrapper) : null;
    const pageWrapperCanScroll =
      pageWrapper &&
      pageWrapper.scrollHeight > pageWrapper.clientHeight &&
      /(auto|scroll|overlay)/.test(pageWrapperStyle.overflowY);
    const scrollContainer = pageWrapperCanScroll ? pageWrapper : document.scrollingElement;
    const usesWindowScroll =
      scrollContainer === document.scrollingElement ||
      scrollContainer === document.documentElement ||
      scrollContainer === document.body;
    let frameId = 0;
    let isVisible = false;
    let lastScrollY = Number.NaN;
    let lastWidth = Number.NaN;
    let lastHeight = Number.NaN;
    let lastMode = '';
    let lastScrollChangeAt = performance.now();
    let settledScrollY = Number.NaN;
    let snapping = false;
    let snapTarget = Number.NaN;
    let lastSnapDistance = Number.POSITIVE_INFINITY;
    let horizontalMetrics = null;
    let renderedProgress = Number.NaN;
    let lastRenderAt = performance.now();
    let activeStageIndex = -1;
    let pointerStart = null;

    const clamp = (value, minimum = 0, maximum = 1) => Math.min(maximum, Math.max(minimum, value));

    function getScrollY() {
      return usesWindowScroll ? window.scrollY : scrollContainer.scrollTop;
    }

    function scrollToY(top, behavior = 'smooth') {
      if (usesWindowScroll) {
        window.scrollTo({ top, behavior });
        return;
      }

      scrollContainer.scrollTo({ top, behavior });
    }

    function setActiveStage(index) {
      const nextIndex = clamp(index, 0, Math.max(0, stages.length - 1));
      if (nextIndex === activeStageIndex) return;

      activeStageIndex = nextIndex;

      stages.forEach((stage, stageIndex) => {
        if (stageIndex === nextIndex) {
          stage.setAttribute('data-br-our-story-active', 'true');
        } else {
          stage.removeAttribute('data-br-our-story-active');
        }
      });

      dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === nextIndex;
        dot.classList.toggle('is-active', isActive);

        if (isActive) {
          dot.setAttribute('aria-current', 'step');
        } else {
          dot.removeAttribute('aria-current');
        }
      });

      if (nextButton) {
        const isLastStage = nextIndex >= stages.length - 1;
        nextButton.disabled = isLastStage;
        nextButton.setAttribute('aria-disabled', String(isLastStage));
      }
    }

    function getScrollConfig() {
      if (window.innerWidth >= 1920) return { startOffset: 300, range: 600 };
      return { startOffset: 260, range: 320 };
    }

    function resetPathFireflies() {
      pathItems.forEach(({ firefly }) => {
        firefly.style.setProperty('offset-distance', '0%');
      });
    }

    function resetHorizontalJourney() {
      timeline.style.removeProperty('--br-horizontal-height');
      timeline.style.removeProperty('--br-horizontal-viewport-height');
      timeline.style.removeProperty('--br-horizontal-card-width');
      timeline.style.removeProperty('--br-horizontal-card-gap');
      timeline.style.removeProperty('--br-horizontal-side-space');
      timeline.style.removeProperty('--br-horizontal-media-share');
      timeline.style.removeProperty('--br-horizontal-track-x');
      timeline.style.removeProperty('--br-horizontal-bee-x');
      timeline.style.removeProperty('--br-horizontal-bee-y');

      stages.forEach((stage) => {
        stage.style.removeProperty('--br-stage-opacity');
        stage.style.removeProperty('--br-stage-rise');
        stage.style.removeProperty('--br-stage-scale');
        stage.removeAttribute('data-br-our-story-active');
      });

      dots.forEach((dot) => {
        dot.classList.remove('is-active');
        dot.removeAttribute('aria-current');
      });

      activeStageIndex = -1;
      horizontalMetrics = null;
      renderedProgress = Number.NaN;
      lastRenderAt = performance.now();
      snapping = false;
      snapTarget = Number.NaN;
      lastSnapDistance = Number.POSITIVE_INFINITY;
    }

    function updateHorizontalJourney(height) {
      const viewportWidth = journey.clientWidth || window.innerWidth;
      const isCompact = window.innerWidth < 750;
      const wideBlend = isCompact ? 0 : clamp((viewportWidth - 900) / 250);
      const singleCardWidth = clamp(viewportWidth * 0.6, 520, 660);
      const overviewCardWidth = clamp(viewportWidth * 0.38, 430, 460);
      const cardWidth = isCompact
        ? clamp(viewportWidth - 48, 280, 420)
        : singleCardWidth + (overviewCardWidth - singleCardWidth) * wideBlend;
      const singleGap = clamp(viewportWidth * 0.035, 28, 42);
      const gap = isCompact ? 18 : singleGap + (12 - singleGap) * wideBlend;
      const centeredSideSpace = Math.max(0, (viewportWidth - cardWidth) * 0.5);
      const overviewSideSpace = clamp(viewportWidth * 0.065, 56, 80);
      const sideSpace = isCompact
        ? centeredSideSpace
        : centeredSideSpace + (overviewSideSpace - centeredSideSpace) * wideBlend;
      const step = cardWidth + gap;
      const distance = Math.max(0, step * (stages.length - 1));
      const viewportTop = usesWindowScroll ? 0 : scrollContainer.getBoundingClientRect().top;
      const travelled = viewportTop - journey.getBoundingClientRect().top;
      const rawProgress = travelled / Math.max(1, distance);
      const progress = clamp(rawProgress);
      const currentScrollY = getScrollY();

      timeline.style.setProperty('--br-horizontal-height', `${Math.ceil(height + distance)}px`);
      timeline.style.setProperty('--br-horizontal-viewport-height', `${height}px`);
      timeline.style.setProperty('--br-horizontal-card-width', `${cardWidth.toFixed(2)}px`);
      timeline.style.setProperty('--br-horizontal-card-gap', `${gap.toFixed(2)}px`);
      timeline.style.setProperty('--br-horizontal-side-space', `${sideSpace.toFixed(2)}px`);
      timeline.style.setProperty('--br-horizontal-media-share', `${(44 + wideBlend * 4).toFixed(2)}%`);

      const timelineWidth = horizontalTimeline ? horizontalTimeline.clientWidth : 0;
      const beeWidth = horizontalFirefly ? horizontalFirefly.offsetWidth : 0;
      const beeHeight = horizontalFirefly ? horizontalFirefly.offsetHeight : 0;
      const waveHeight = horizontalTimeline ? horizontalTimeline.clientHeight : 0;

      horizontalMetrics = {
        distance,
        journeyStartScroll: currentScrollY - travelled,
        progress,
        rawProgress,
        step,
        beeTravelX: Math.max(0, timelineWidth - beeWidth),
        beeHeight,
        waveCenter: waveHeight * 0.5,
        waveAmplitude: waveHeight * 0.38,
        wideBlend,
      };

      if (!Number.isFinite(renderedProgress)) {
        renderedProgress = progress;
        renderHorizontalJourney(progress);
      }
    }

    function renderHorizontalJourney(progress) {
      if (!horizontalMetrics) return;

      const panelPosition = progress * Math.max(0, stages.length - 1);
      const activeIndex = Math.round(panelPosition);

      timeline.style.setProperty(
        '--br-horizontal-track-x',
        `${(-progress * horizontalMetrics.distance).toFixed(2)}px`
      );

      stages.forEach((stage, index) => {
        const distanceFromCenter = Math.abs(panelPosition - index);
        const focus = clamp(1 - distanceFromCenter);
        const restingOpacity = 0.34 + horizontalMetrics.wideBlend * 0.48;
        const restingScale = 0.88 + horizontalMetrics.wideBlend * 0.08;
        const restingRise = 18 - horizontalMetrics.wideBlend * 10;
        const opacity = restingOpacity + focus * (1 - restingOpacity);
        const scale = restingScale + focus * (1 - restingScale);
        const rise = (1 - focus) * restingRise;

        stage.style.setProperty('--br-stage-opacity', opacity.toFixed(3));
        stage.style.setProperty('--br-stage-rise', `${rise.toFixed(2)}px`);
        stage.style.setProperty('--br-stage-scale', scale.toFixed(3));
      });

      setActiveStage(activeIndex);

      const beeX = progress * horizontalMetrics.beeTravelX;
      const beeY =
        horizontalMetrics.waveCenter -
        horizontalMetrics.waveAmplitude * Math.sin(progress * Math.PI * 4) -
        horizontalMetrics.beeHeight * 0.5;

      timeline.style.setProperty('--br-horizontal-bee-x', `${beeX.toFixed(2)}px`);
      timeline.style.setProperty('--br-horizontal-bee-y', `${beeY.toFixed(2)}px`);
    }

    function renderHorizontalFrame(now, force = false) {
      if (reducedMotion.matches || lastMode !== 'horizontal' || !horizontalMetrics) return;

      const targetProgress = horizontalMetrics.progress;

      if (!Number.isFinite(renderedProgress) || force) {
        renderedProgress = targetProgress;
      } else {
        const elapsed = clamp(now - lastRenderAt, 0, 64);
        const damping = 1 - Math.exp(-elapsed / 72);
        renderedProgress += (targetProgress - renderedProgress) * damping;

        if (Math.abs(targetProgress - renderedProgress) < 0.0002) {
          renderedProgress = targetProgress;
        }
      }

      lastRenderAt = now;
      renderHorizontalJourney(renderedProgress);
    }

    function snapToStage(index) {
      if (!horizontalMetrics || reducedMotion.matches || window.innerWidth >= 1200) return;

      const safeIndex = clamp(index, 0, Math.max(0, stages.length - 1));
      const target = horizontalMetrics.journeyStartScroll + horizontalMetrics.step * safeIndex;
      const currentScrollY = getScrollY();

      snapping = true;
      snapTarget = target;
      lastSnapDistance = Math.abs(currentScrollY - target);
      settledScrollY = Number.NaN;
      scrollToY(target);
    }

    function maybeSnapHorizontal(now) {
      if (reducedMotion.matches || lastMode !== 'horizontal' || !horizontalMetrics) return;

      const currentScrollY = getScrollY();

      if (snapping) {
        const distanceToTarget = Math.abs(currentScrollY - snapTarget);

        if (distanceToTarget <= 2) {
          snapping = false;
          settledScrollY = currentScrollY;
          lastScrollChangeAt = now;
        } else if (distanceToTarget > lastSnapDistance + 2) {
          snapping = false;
          snapTarget = Number.NaN;
          settledScrollY = Number.NaN;
          lastScrollChangeAt = now;
        }

        lastSnapDistance = distanceToTarget;
        return;
      }

      if (
        horizontalMetrics.rawProgress < 0 ||
        horizontalMetrics.rawProgress > 1 ||
        now - lastScrollChangeAt < 260 ||
        Math.abs(currentScrollY - settledScrollY) < 0.5
      ) {
        return;
      }

      const targetIndex = Math.round(horizontalMetrics.progress * Math.max(0, stages.length - 1));
      const target = horizontalMetrics.journeyStartScroll + horizontalMetrics.step * targetIndex;

      if (Math.abs(currentScrollY - target) <= 2) {
        settledScrollY = currentScrollY;
        return;
      }

      snapToStage(targetIndex);
    }

    function updatePathFireflies(height) {
      const { startOffset, range } = getScrollConfig();
      const startY = height - startOffset;

      pathItems.forEach(({ path, firefly }) => {
        const progress = clamp((startY - path.getBoundingClientRect().top) / range);
        firefly.style.setProperty('offset-distance', `${(progress * 100).toFixed(3)}%`);
      });
    }

    function updateMotion(force = false) {
      const scrollY = getScrollY();
      const width = window.innerWidth;
      const height = usesWindowScroll ? window.innerHeight : scrollContainer.clientHeight;
      const mode = width < 1200 ? 'horizontal' : 'paths';
      const scrollChanged = scrollY !== lastScrollY;

      if (scrollChanged) {
        lastScrollChangeAt = performance.now();
        settledScrollY = Number.NaN;

        if (snapping) {
          const distanceToTarget = Math.abs(scrollY - snapTarget);

          if (distanceToTarget > lastSnapDistance + 2) {
            snapping = false;
            snapTarget = Number.NaN;
          }

          lastSnapDistance = distanceToTarget;
        }
      }

      if (!force && scrollY === lastScrollY && width === lastWidth && height === lastHeight && mode === lastMode) return;

      lastScrollY = scrollY;
      lastWidth = width;
      lastHeight = height;
      lastMode = mode;

      if (reducedMotion.matches) {
        resetPathFireflies();
        resetHorizontalJourney();
        return;
      }

      if (mode === 'horizontal') {
        resetPathFireflies();
        updateHorizontalJourney(height);
        return;
      }

      resetHorizontalJourney();
      updatePathFireflies(height);
    }

    function tick() {
      frameId = 0;
      if (!isVisible) return;

      const now = performance.now();
      updateMotion();
      renderHorizontalFrame(now);
      maybeSnapHorizontal(now);
      frameId = window.requestAnimationFrame(tick);
    }

    function startTicking() {
      if (!frameId) frameId = window.requestAnimationFrame(tick);
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        isVisible = entry.isIntersecting;

        if (isVisible) {
          updateMotion(true);
          renderHorizontalFrame(performance.now(), true);
          startTicking();
        } else if (frameId) {
          window.cancelAnimationFrame(frameId);
          frameId = 0;
        }
      },
      {
        root: usesWindowScroll ? null : scrollContainer,
        rootMargin: '200px 0px',
      }
    );

    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        updateMotion(true);
        snapToStage(index);
      });
    });

    if (nextButton) {
      nextButton.addEventListener('click', () => {
        updateMotion(true);
        snapToStage(activeStageIndex + 1);
      });
    }

    if (horizontalViewport) {
      horizontalViewport.addEventListener('pointerdown', (event) => {
        if (
          window.innerWidth >= 1200 ||
          reducedMotion.matches ||
          !horizontalMetrics ||
          (event.pointerType !== 'touch' && event.pointerType !== 'pen') ||
          event.target.closest('button')
        ) {
          return;
        }

        pointerStart = {
          id: event.pointerId,
          x: event.clientX,
          y: event.clientY,
          scrollY: getScrollY(),
          stageIndex: horizontalMetrics
            ? Math.round(horizontalMetrics.progress * Math.max(0, stages.length - 1))
            : activeStageIndex,
          dragging: false,
        };
        horizontalViewport.setPointerCapture(event.pointerId);
      });

      horizontalViewport.addEventListener(
        'pointermove',
        (event) => {
          if (!pointerStart || event.pointerId !== pointerStart.id) return;

          const deltaX = event.clientX - pointerStart.x;
          const deltaY = event.clientY - pointerStart.y;

          if (!pointerStart.dragging) {
            if (Math.abs(deltaX) < 8 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.15) return;

            pointerStart.dragging = true;
            snapping = false;
            snapTarget = Number.NaN;
            settledScrollY = Number.NaN;
          }

          event.preventDefault();
          scrollToY(pointerStart.scrollY - deltaX, 'auto');
        },
        { passive: false }
      );

      horizontalViewport.addEventListener('pointerup', (event) => {
        if (!pointerStart || event.pointerId !== pointerStart.id) return;

        const deltaX = event.clientX - pointerStart.x;
        const deltaY = event.clientY - pointerStart.y;
        const startStageIndex = pointerStart.stageIndex;
        const wasDragging = pointerStart.dragging;
        pointerStart = null;

        updateMotion(true);

        if (!wasDragging || Math.abs(deltaX) <= Math.abs(deltaY) * 1.15) return;

        const nearestIndex = horizontalMetrics
          ? Math.round(horizontalMetrics.progress * Math.max(0, stages.length - 1))
          : activeStageIndex;
        let targetIndex = nearestIndex;

        if (Math.abs(deltaX) >= 48) {
          const directionalIndex = startStageIndex + (deltaX < 0 ? 1 : -1);
          targetIndex = deltaX < 0
            ? Math.max(nearestIndex, directionalIndex)
            : Math.min(nearestIndex, directionalIndex);
        }

        snapToStage(targetIndex);
      });

      horizontalViewport.addEventListener('pointercancel', () => {
        const wasDragging = pointerStart?.dragging;
        pointerStart = null;

        if (wasDragging && horizontalMetrics) {
          updateMotion(true);
          snapToStage(Math.round(horizontalMetrics.progress * Math.max(0, stages.length - 1)));
        }
      });
    }

    observer.observe(timeline);
    reducedMotion.addEventListener('change', () => updateMotion(true));
    window.addEventListener('load', () => updateMotion(true), { once: true });
    window.addEventListener('pageshow', () => updateMotion(true));

    updateMotion(true);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initBrOurStoryMotion(), { once: true });
} else {
  initBrOurStoryMotion();
}

document.addEventListener('shopify:section:load', (event) => initBrOurStoryMotion(event.target));
